// ==================== Navigation.js ====================
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navigation = ({ showFloatingNav, activeSection, scrollToSection }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  const navItems = ['Home', 'Process', 'Pricing', 'Features', 'Companies', 'Testimonials', 'About'];
  
  // Map navigation items to their correct section indices
// Map navigation items to their correct section indices
const getSectionIndex = (navIndex) => {
  const sectionMap = {
    0: 0, // Home -> section-1 (HeroSection)
    1: 1, // Process -> section-2 (ProcessSection)
    2: 2, // Pricing -> section-3 (PricingSection)
    3: 3, // Features -> section-7 (FeaturesSection)
    4: 4, // Companies -> section-4 (CompaniesSection)
    5: 5, // Testimonials -> section-5 (TestimonialsSection)
    6: 6  // About -> section-6 (AboutSection)
  };
  return sectionMap[navIndex];
};

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavClick = (index) => {
    // If we're not on home page, navigate to home first
    if (location.pathname !== '/') {
      window.location.href = '/';
      return;
    }
    
    const sectionIndex = getSectionIndex(index);
    scrollToSection(sectionIndex);
    setIsMobileMenuOpen(false); // Close mobile menu after selection
  };

  // Check if current navigation item is active
  const isNavItemActive = (navIndex) => {
    const sectionIndex = getSectionIndex(navIndex);
    return activeSection === sectionIndex && location.pathname === '/';
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav className={`
        fixed top-3 sm:top-6 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-500
        hidden md:block px-4 sm:px-0
        ${showFloatingNav ? 'translate-y-0 opacity-100' : '-translate-y-20 opacity-0'}
      `}>
        <div className="bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl px-4 sm:px-8 py-3 sm:py-4 shadow-xl border border-gray-100">
          <div className="flex items-center gap-4 sm:gap-8">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center relative overflow-hidden"
                   style={{
                     background: 'linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)',
                     boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)'
                   }}>
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-md sm:rounded-lg flex items-center justify-center">
                  <div className="text-lg sm:text-2xl font-bold"
                       style={{
                         background: 'linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)',
                         WebkitBackgroundClip: 'text',
                         WebkitTextFillColor: 'transparent',
                         fontFamily: 'Inter, sans-serif'
                       }}>
                    Z
                  </div>
                </div>
              </div>
              <span className="text-lg sm:text-xl font-bold text-gray-900">ZENTRY</span>
            </Link>
            
            <div className="flex items-center gap-3 sm:gap-6">
              {navItems.map((item, index) => (
                <button
                  key={item}
                  onClick={() => handleNavClick(index)}
                  className={`
                    px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300
                    ${isNavItemActive(index)
                      ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white" 
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    }
                  `}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className={`
        fixed top-3 left-4 right-4 z-50 transition-all duration-500 md:hidden
        ${showFloatingNav ? 'translate-y-0 opacity-100' : '-translate-y-20 opacity-0'}
      `}>
        <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-xl border border-gray-100">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center relative overflow-hidden"
                   style={{
                     background: 'linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)',
                     boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)'
                   }}>
                <div className="w-6 h-6 bg-white rounded-md flex items-center justify-center">
                  <div className="text-lg font-bold"
                       style={{
                         background: 'linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)',
                         WebkitBackgroundClip: 'text',
                         WebkitTextFillColor: 'transparent',
                         fontFamily: 'Inter, sans-serif'
                       }}>
                    Z
                  </div>
                </div>
              </div>
              <span className="text-lg font-bold text-gray-900">ZENTRY</span>
            </Link>
            
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200 border border-gray-200"
            >
              {isMobileMenuOpen ? 
                <X size={20} className="text-gray-700" /> : 
                <Menu size={20} className="text-gray-700" />
              }
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`
        fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 md:hidden
        ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
      `} onClick={() => setIsMobileMenuOpen(false)} />

      {/* Mobile Menu Dropdown */}
      <div className={`
        fixed top-20 left-4 right-4 z-50 md:hidden transition-all duration-300
        ${isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}
      `}>
        <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-xl border border-gray-100">
          <div className="space-y-2">
            {navItems.map((item, index) => (
              <button
                key={item}
                onClick={() => handleNavClick(index)}
                className={`
                  w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-all duration-300
                  ${isNavItemActive(index)
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white" 
                    : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  }
                `}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
